import { Head, Link, router } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface Category {
  id: number
  name: string
}

interface Report {
  id: number
  name: string
  link: string
  description: string | null
  notes: string | null
  status: number
  admin_only: boolean
  categories: Category[]
}

interface Props {
  reports: Report[]
  categories: Category[]
  filters: {
    category: string
    keyword: string
  }
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Manage", href: "/admin/reports" },
  { title: "Reports", href: "/admin/reports" },
]

export default function Index({ reports, categories, filters }: Props) {
  const handleFilter = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    router.get(
      "/admin/reports",
      {
        category: formData.get("category") as string,
        keyword: formData.get("keyword") as string,
      },
      { preserveState: true },
    )
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Reports" />

      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">Reports</h1>
          <Link
            href="/admin/reports/new"
            className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
          >
            Add Report
          </Link>
        </div>

        <form onSubmit={handleFilter} className="mb-4 flex items-end gap-4">
          <div>
            <label
              htmlFor="category"
              className="mb-1 block text-sm font-medium"
            >
              Category
            </label>
            <select
              id="category"
              name="category"
              defaultValue={filters.category}
              className="border-hpi-light-gray rounded border px-3 py-2"
            >
              <option value="">All Categories</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="keyword" className="mb-1 block text-sm font-medium">
              Keyword
            </label>
            <input
              type="text"
              id="keyword"
              name="keyword"
              defaultValue={filters.keyword}
              placeholder="Search reports..."
              className="border-hpi-light-gray rounded border px-3 py-2"
            />
          </div>
          <button
            type="submit"
            className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
          >
            Search
          </button>
        </form>

        {reports.length > 0 ? (
          <table className="border-hpi-light-gray w-full border-collapse border">
            <thead>
              <tr className="bg-hpi-pale-gray">
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Name
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Link
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Description
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Categories
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Status
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {reports.map((report) => (
                <tr
                  key={report.id}
                  className={`hover:bg-hpi-pale-blue ${report.status !== 0 ? "opacity-50" : ""}`}
                >
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {report.name}
                    {report.admin_only && (
                      <span className="text-hpi-red ml-2 text-xs font-medium">
                        (Admin Only)
                      </span>
                    )}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {report.link && (
                      <a
                        href={report.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-hpi-active-blue hover:underline"
                      >
                        {report.link}
                      </a>
                    )}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {report.description}
                    {report.notes && (
                      <div className="text-hpi-med-gray mt-1 text-sm">
                        {report.notes}
                      </div>
                    )}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {report.categories.map((c) => c.name).join(", ")}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {report.status === 0 ? "Active" : "Inactive"}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    <Link
                      href={`/admin/reports/${report.id}/edit`}
                      className="text-hpi-active-blue hover:underline"
                    >
                      Edit
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-hpi-med-gray">No reports found.</p>
        )}
      </div>
    </AppLayout>
  )
}
